/**
 * Capacitor Native Bridge
 * Handles native functionality for Android app
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { PushNotifications } from '@capacitor/push-notifications';
import { Browser } from '@capacitor/browser';

// Check if running in native app
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'android', 'ios', 'web'

/**
 * Initialize native app features
 */
export const initializeApp = async () => {
  if (!isNative) return;

  try {
    // Hide splash screen after app loads
    await SplashScreen.hide();

    // Configure status bar
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#1e40af' });

    // Handle back button
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('[Native] App state:', isActive ? 'active' : 'background');
    });

    // Handle deep links
    App.addListener('appUrlOpen', ({ url }) => {
      console.log('[Native] Deep link:', url);
      // Handle deep link navigation here
      const path = url.replace('https://bodycorp365.com', '');
      if (path) {
        window.location.href = path;
      }
    });

    // Keyboard handling
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.paddingBottom = `${info.keyboardHeight}px`;
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.paddingBottom = '0px';
    });

    console.log('[Native] App initialized successfully');
  } catch (error) {
    console.error('[Native] Initialization error:', error);
  }
};

/**
 * Request push notification permissions and register
 */
export const initializePushNotifications = async () => {
  if (!isNative) return null;

  try {
    // Request permission
    const permStatus = await PushNotifications.requestPermissions();
    
    if (permStatus.receive === 'granted') {
      // Register with FCM
      await PushNotifications.register();

      // Get FCM token
      PushNotifications.addListener('registration', (token) => {
        console.log('[Push] FCM Token:', token.value);
        // Save token to backend
        savePushToken(token.value);
      });

      // Handle registration errors
      PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] Registration error:', error);
      });

      // Handle push notifications received
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[Push] Received:', notification);
      });

      // Handle notification tap
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('[Push] Action:', notification);
        // Navigate based on notification data
        const data = notification.notification.data;
        if (data?.route) {
          window.location.href = data.route;
        }
      });

      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[Push] Error:', error);
    return false;
  }
};

/**
 * Save push token to backend
 */
const savePushToken = async (token) => {
  try {
    const sessionToken = localStorage.getItem('session_token');
    if (!sessionToken) return;

    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      },
      body: JSON.stringify({ token, platform: platform })
    });
  } catch (error) {
    console.error('[Push] Failed to save token:', error);
  }
};

/**
 * Trigger haptic feedback
 */
export const hapticFeedback = async (style = 'light') => {
  if (!isNative) return;
  
  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    }[style] || ImpactStyle.Light;
    
    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.error('[Haptic] Error:', error);
  }
};

/**
 * Open external link in browser
 */
export const openExternalLink = async (url) => {
  if (isNative) {
    await Browser.open({ url });
  } else {
    window.open(url, '_blank');
  }
};

/**
 * Share content using native share sheet
 */
export const shareContent = async (title, text, url) => {
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
    } catch (error) {
      console.error('[Share] Error:', error);
    }
  }
};

/**
 * Get app info
 */
export const getAppInfo = async () => {
  if (!isNative) return null;
  
  try {
    const info = await App.getInfo();
    return {
      name: info.name,
      id: info.id,
      version: info.version,
      build: info.build
    };
  } catch (error) {
    console.error('[App] Info error:', error);
    return null;
  }
};

export default {
  isNative,
  platform,
  initializeApp,
  initializePushNotifications,
  hapticFeedback,
  openExternalLink,
  shareContent,
  getAppInfo
};
